import './tokens.css';

/**
 * The same tokens, for styled-components.
 *
 * Every value here is the CSS custom property, not a literal — one definition, in tokens.css, and
 * this is only how a `styled.div` says its name without typing `var(--dx-…)` by hand. Import the
 * module for its side effect (the stylesheet) and destructure what you need:
 *
 *   import { DX } from '../../theme/v2';
 *   const Panel = styled.div`
 *     border: ${DX.hairline};
 *     border-radius: ${DX.radius.card};
 *     background: ${DX.surface};
 *   `;
 *
 * The page's own root must carry `className="dx-v2"`, or none of these resolve — that scope is what
 * keeps the legacy pages looking exactly as they did.
 */
export const DX_ROOT_CLASS = 'dx-v2';

export const DX = {
  surface: 'var(--dx-surface)',
  surfaceSunken: 'var(--dx-surface-sunken)',
  surfaceBand: 'var(--dx-surface-band)',
  surfaceChrome: 'var(--dx-surface-chrome)',

  hover: 'var(--dx-hover)',
  active: 'var(--dx-active)',
  selected: 'var(--dx-selected)',

  border: 'var(--dx-border)',
  borderSoft: 'var(--dx-border-soft)',
  borderStrong: 'var(--dx-border-strong)',
  hairline: 'var(--dx-hairline)',
  hairlineSoft: 'var(--dx-hairline-soft)',

  text: 'var(--dx-text)',
  textMuted: 'var(--dx-text-muted)',
  text7: 'var(--dx-text-7)',
  textFaint: 'var(--dx-text-faint)',
  textInverse: 'var(--dx-text-inverse)',

  tone: {
    done: 'var(--dx-tone-done)',
    next: 'var(--dx-tone-next)',
    running: 'var(--dx-tone-running)',
    attention: 'var(--dx-tone-attention)',
    idle: 'var(--dx-tone-idle)',
    ok: 'var(--dx-tone-ok)',
    warning: 'var(--dx-tone-warning)'
  },

  accent: 'var(--dx-accent)',
  accentSoft: 'var(--dx-accent-soft)',

  fz: {
    caption: 'var(--dx-fz-caption)',
    xs: 'var(--dx-fz-xs)',
    sm: 'var(--dx-fz-sm)',
    md: 'var(--dx-fz-md)',
    lg: 'var(--dx-fz-lg)'
  },

  lh: {
    caption: 'var(--dx-lh-caption)',
    xs: 'var(--dx-lh-xs)',
    sm: 'var(--dx-lh-sm)',
    md: 'var(--dx-lh-md)'
  },

  fw: {
    regular: 'var(--dx-fw-regular)',
    medium: 'var(--dx-fw-medium)',
    strong: 'var(--dx-fw-strong)',
    caption: 'var(--dx-fw-caption)'
  },

  captionTracking: 'var(--dx-caption-tracking)',

  space: {
    1: 'var(--dx-space-1)',
    2: 'var(--dx-space-2)',
    3: 'var(--dx-space-3)',
    4: 'var(--dx-space-4)',
    5: 'var(--dx-space-5)',
    6: 'var(--dx-space-6)',
    7: 'var(--dx-space-7)'
  },

  radius: {
    control: 'var(--dx-radius-control)',
    item: 'var(--dx-radius-item)',
    card: 'var(--dx-radius-card)'
  },

  row: {
    tight: 'var(--dx-row-tight)',
    base: 'var(--dx-row)',
    loose: 'var(--dx-row-loose)'
  },

  control: {
    sm: 'var(--dx-control-sm)',
    base: 'var(--dx-control)',
    lg: 'var(--dx-control-lg)'
  },

  bar: {
    chrome: 'var(--dx-bar-chrome)',
    stepper: 'var(--dx-bar-stepper)',
    toolbar: 'var(--dx-bar-toolbar)',
    status: 'var(--dx-bar-status)'
  },

  duration: 'var(--dx-duration)',
  ease: 'var(--dx-ease)',

  /** One line for a caption: 11/700 uppercase, tracked, muted. Written out on every V2 page so far. */
  caption: `
    font-size: var(--dx-fz-caption);
    font-weight: var(--dx-fw-caption);
    line-height: var(--dx-lh-caption);
    letter-spacing: var(--dx-caption-tracking);
    text-transform: uppercase;
    color: var(--dx-text-muted);
  `
};
